/**
 * How the No Relief reminder gets this user's attention, per session:
 *
 *  - 'popup' (default): the popup 30 minutes before a counter goes unmanned, and the badge on the
 *    No Relief button;
 *  - 'badge': no popup — only the badge counts the calls still owed;
 *  - 'off': neither. The No Relief list itself is still there to open.
 *
 * Personal to this user and remembered on this device, one setting per session ID: someone who has
 * a session open only to watch can quiet it without quieting the person making the calls.
 */
import { useSyncExternalStore } from 'react';
import { loadPref, savePref } from './prefStorage';

export type NoReliefAlert = 'popup' | 'badge' | 'off';

export const NO_RELIEF_ALERT_OPTIONS: Array<{ value: NoReliefAlert; label: string; hint: string }> = [
  { value: 'popup', label: 'Popup + badge', hint: 'The reminder pops up 30 minutes before a counter goes unmanned.' },
  { value: 'badge', label: 'Badge only', hint: 'No popup; the No Relief button still counts the calls owed.' },
  { value: 'off', label: 'Off', hint: 'No popup and no badge. The No Relief list is still there to open.' },
];

const keyFor = (sessionId: string) => `orca.noReliefAlert.${sessionId}`;
const listeners = new Set<() => void>();

export function getNoReliefAlert(sessionId: string | null | undefined): NoReliefAlert {
  if (!sessionId) return 'popup';
  const v = loadPref(keyFor(sessionId));
  return v === 'badge' || v === 'off' ? v : 'popup';
}

export function setNoReliefAlert(sessionId: string, value: NoReliefAlert): void {
  savePref(keyFor(sessionId), value);
  listeners.forEach((l) => l());
}

/** This user's No Relief alert for `sessionId`, re-rendering the caller when it changes. */
export function useNoReliefAlert(sessionId: string | null | undefined): NoReliefAlert {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => getNoReliefAlert(sessionId),
    () => 'popup',
  );
}
